import React, { useState } from 'react';
import { FiCalendar, FiCheck, FiChevronDown, FiEdit3 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { t } from 'i18next';
import usePermission from '@/app/hooks/usePermission';
import moment from 'moment';
import { statusBadgeText } from './utils/BadgeEssay';
import tag from './img/tag_novo.png';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { Card, CardBody, CardBodyContent, CardBodyHeader, CardBodyHeaderContent, CardContent, CardFooter, CardIcon, CardIconImg, CardKpiGrid, CardKpiTitle, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FormSwitch } from '@/components/custom/form';
import { ButtonGroup } from '@/components/ui/button-group';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export function EssayCard(props) {
  const { essay, isAdmin, deleteEssay } = props;
  const [unpublishWarning, setUnpublishWarning] = useState(false);
  const [publishWarning, setPublishWarning] = useState(false);
  const [loading, setLoading] = useState(false);

  const client = useClient();

  const accepting_proposal = essay?.status === 'accepting_proposal';
  const reviewing = essay?.status === 'reviewing';

  const grade = essay?.['essay-student']?.grade ?? 0;

  const statusBadgeColor = {
    accepting_proposal: 'attention',
    close_for_submissions: 'neutral',
    processing: 'warning',
    unreviewed: 'success',
    reviewed: 'success',
    annulled: 'danger',
    annulled_by_teacher: 'danger ',
    minimum_length_not_satisfied: 'danger',
    failed_to_process: 'danger',
    reviewing: 'warning',
    not_sent: 'neutral',
    ocr_review: 'warning',
    sending_by_teacher: 'warning'
  };

  let badgeColor = '';
  let badgeText = '';

  if (accepting_proposal) {
    if (moment(essay?.['publish-at']).isAfter(moment())) {
      badgeText = 'Agendada';
      badgeColor = 'warning';
    } else {
      badgeText = statusBadgeText[essay?.status];
      badgeColor = 'attention';
    }
  } else if (reviewing) {
    badgeColor = 'success';
    badgeText = statusBadgeText.unreviewed;
  } else {
    badgeColor = statusBadgeColor[essay?.status] ?? 'success';
    badgeText = statusBadgeText[essay?.status];
  }

  const essayCreatedAtlessThan15Days = essay => moment().diff(essay?.['created-at'], 'days') <= 15;

  const updateEssayPublicity = async () => {
    setLoading(true);
    const { error } = await client.mutate(
      `essays/${essay.id}/toggle_visibility`,
      {},
      {
        method: 'PATCH'
      }
    );
    if (!error) {
      toast.success(!essay.visible ? 'Publicado com sucesso!' : 'Despublicado com sucesso!');
    } else {
      toast.error('Erro ao publicar redação');
    }
    setLoading(false);
    essay.visible = !essay.visible;
    setUnpublishWarning(false);
    setPublishWarning(false);
  };

  const handlePublishChange = () => async () => {
    if (!essay.visible) {
      setUnpublishWarning(true);
    } else if (essay.visible) {
      setPublishWarning(true);
    } else {
      await updateEssayPublicity();
    }
  };

  return (
    <Card>
      <CardContent>
        {essayCreatedAtlessThan15Days(essay) && (
          <img
            src={tag}
            className="absolute -top-5 w-[60px]"
          />
        )}

        <CardIcon>
          <CardIconImg>
            <FiEdit3 />
          </CardIconImg>
        </CardIcon>

        <CardBody>
          <CardBodyHeader>
            <CardBodyHeaderContent>
              <CardTitle title={essay?.topic}>{essay?.topic}</CardTitle>
            </CardBodyHeaderContent>

            <Badge
              size="xs"
              title={badgeText}
              variant={badgeColor}
            >
              {badgeText}
            </Badge>
          </CardBodyHeader>

          {usePermission('Editar redação', 'Redação') && (
            <FormSwitch
              label={t('lms.enable')}
              id={`publish-${essay.id}`}
              name={`publish-${essay.id}`}
              checked={essay.visible}
              onCheckedChange={handlePublishChange()}
            />
          )}

          <CardBodyContent className="gap-0">
            <CardKpiTitle>
              <FiCheck />
              {'Publicação em '}
              {moment(essay?.['publish-at']).format('DD/MM/YYYY HH:mm')}
            </CardKpiTitle>
            {(accepting_proposal || essay?.status === 'close_for_submissions' || essay?.status === 'ocr_review') && (
              <CardKpiTitle>
                <FiCalendar className="align-sub" />
                {' Até '}
                {moment(essay?.['delivery-date']).format('DD/MM/YYYY')}
              </CardKpiTitle>
            )}
            {['processing', 'annulled', 'annulled_by_teacher'].includes(essay?.status) && (
              <CardKpiGrid>
                <CardKpiTitle>
                  <FiCalendar />
                  {'Em '}
                  {moment(essay?.['essay-student']?.['delivery-date']).format('DD/MM/YYYY')}
                </CardKpiTitle>
                <CardKpiTitle>{essay?.['essay-student']?.['submission_order']}° envio</CardKpiTitle>
              </CardKpiGrid>
            )}
            {['reviewed', 'unreviewed', 'reviewing'].includes(essay?.status) && (
              <CardKpiGrid>
                <CardKpiTitle>
                  <FiCheck />
                  {'Pontuação '}
                  {grade}
                </CardKpiTitle>
                <CardKpiTitle>{essay?.['essay-student']?.['submission_order']}° envio</CardKpiTitle>
              </CardKpiGrid>
            )}
          </CardBodyContent>
        </CardBody>
      </CardContent>

      <CardFooter>
        {isAdmin ? (
          <ButtonGroup className="w-full">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              asChild
            >
              <Link to={`/redacao/acompanhamento/${essay?.id}`}>{t('button.access')}</Link>
            </Button>
            {usePermission('Excluir redação', 'Redação') || usePermission('Editar redação', 'Redação') ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                  >
                    <FiChevronDown size={20} />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  {usePermission('Editar redação', 'Redação') && (
                    <DropdownMenuItem asChild>
                      <Link to={`/redacao/editar/curso/${essay?.id}`}>{t('button.edit')}</Link>
                    </DropdownMenuItem>
                  )}
                  {usePermission('Excluir redação', 'Redação') && <DropdownMenuItem onClick={() => deleteEssay(essay?.id)}>{t('button.delete')}</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </ButtonGroup>
        ) : essay?.status === 'reviewed' || essay.status === 'unreviewed' || essay.status === 'reviewing' ? (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            asChild
          >
            <Link to={`/redacao/consulta/${essay?.id}/${essay?.['essay-student']?.id}`}>Acessar</Link>
          </Button>
        ) : essay.status === 'ocr_review' ? (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            asChild
          >
            <Link to={`/redacao/enviar/${essay?.id}`}>Acessar</Link>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            disabled={essay?.status === 'Em correção' || (essay?.status === 'close_for_submissions' && 'disabled')}
            asChild
          >
            <Link to={`/redacao/sobre/${essay?.id}`}>Acessar</Link>
          </Button>
        )}
      </CardFooter>

      <ModalWarning
        isSubmitting={loading}
        show={unpublishWarning}
        titleFirstButton="Sim. Quero habilitar"
        titleSecondButton="Não. Manter desabilitado"
        onClose={() => setUnpublishWarning(false)}
        title={'Você tem certeza que deseja habilitar a visualização?'}
        description={'Ao habilitar, esta redação ficará visível para os alunos'}
        onSubmitFirstButton={() => updateEssayPublicity()}
        onSubmitSecondButton={() => setUnpublishWarning(false)}
      />

      <ModalWarning
        isSubmitting={loading}
        show={publishWarning}
        titleFirstButton="Sim. Quero desabilitar"
        titleSecondButton="Não. Manter habilitado"
        onClose={() => setPublishWarning(false)}
        title={'Você tem certeza que deseja desabilitar a visualização?'}
        description={'Ao desabilitar, esta redação ficará invisível para os alunos'}
        onSubmitFirstButton={() => updateEssayPublicity()}
        onSubmitSecondButton={() => setPublishWarning(false)}
      />
    </Card>
  );
}
